import { API_HOST, STORAGE_KEY } from "@/lib/config"
import type { SolveResponse } from "../types/tutor"
import type { MicrocourseProject } from "@ai-tutor/shared"

const TRPC_BASE = `${API_HOST}/trpc`

async function tRPCRequest<T>(
  procedure: string,
  input?: unknown,
  method?: "GET" | "POST",
): Promise<T> {
  const token = localStorage.getItem(STORAGE_KEY)
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  const url = `${TRPC_BASE}/${procedure}`
  const fetchOptions: RequestInit = { headers }

  if (method === "GET" || (method === undefined && input === undefined)) {
    fetchOptions.method = "GET"
  } else {
    fetchOptions.method = "POST"
    if (input !== undefined) {
      fetchOptions.body = JSON.stringify(input)
    }
  }

  const response = await fetch(url, fetchOptions)
  const data = await response.json()

  if (!response.ok) {
    const errorMessage = data?.error?.message || "请求失败"
    try {
      const parsed = JSON.parse(errorMessage)
      if (Array.isArray(parsed) && parsed[0]?.message) {
        throw new Error(parsed.map((e: any) => e.message).join(", "))
      }
    } catch (e) {
      // Not JSON, use as-is
    }
    throw new Error(errorMessage)
  }

  return data.result.data as T
}

export interface SolveRequest {
  content: string
  image_url?: string
  /** 后端 solveInput schema 实际接收的键名 */
  image?: string
  question_id?: string
}

export interface TTSRequest {
  text: string
  step_id: string
}

export interface TTSResponse {
  audio_url: string
  duration: number
}

export interface ShareCreate {
  input_content: string
  result_json: SolveResponse
}

export interface ShareResponse {
  share_id: string
  share_url: string
}

export interface SessionResponse {
  input_content: string
  result_json: SolveResponse
  created_at: string
}

export async function solveProblem(data: SolveRequest): Promise<SolveResponse> {
  return tRPCRequest<SolveResponse>("tutor.solve", data)
}

export async function getTTS(data: TTSRequest): Promise<TTSResponse> {
  // Note: TTS endpoint doesn't exist in tRPC yet
  // Fall back to REST endpoint
  const token = localStorage.getItem(STORAGE_KEY)
  const response = await fetch(`${API_HOST}/api/v1/tutor/tts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    throw new Error("TTS request failed")
  }
  return response.json()
}

export async function createShare(data: ShareCreate): Promise<ShareResponse> {
  return tRPCRequest<ShareResponse>("tutor.share", data)
}

export async function getSession(shareId: string): Promise<SessionResponse> {
  return tRPCRequest<SessionResponse>("tutor.getSession", { id: shareId }, "GET")
}

export async function explainAsDeck(data: SolveRequest): Promise<MicrocourseProject> {
  return tRPCRequest<MicrocourseProject>("tutor.explainAsDeck", data)
}
